import React, { useEffect, useState } from "react";
import ProductFilter from "@containers/ProductFilter/ProductFilter";
import ProductMap from "@components/ProductMap/ProductMap";
import { useAuth } from "contexts/AuthContext";
import Helpers from "@utils/Helpers";
import "./ProductCatalog.scss";
import { useUser } from "contexts/UserContext";
import ProductList from "@containers/ProductList/ProductList";
import { ProductApi } from "services/ProductApi";
import { errorLog } from "logging";
import { IProductSearch } from "@utils/Interfaces";
import { Col, Pagination, Row, Select } from "antd";
import { AdApi } from "services/AdApi";

const ProductCatalog: React.FC = () => {
  const { userProfile } = useUser();
  const [allProducts, setAllProducts] = useState<any>(null);
  const [isLargeMode, setIsLargeMode] = useState(false);
  const [productNearBy, setProductNearBy] = useState(null);
  const [isOpenMap, setIsOpenMap] = useState(false);
  const { isAuthenticated } = useAuth();
  const [searchParam, setSearchParam] = useState<IProductSearch>({
    query: "",
    type: null,
    location: null,
    selling_method: null,
    categories_id: [1, 2],
    quantity: null,
    radius: 300,
    address: "",
    filters: {
      hay_cut: [],
      bale_weight_min: 0,
      bale_weight_max: 200,
      shape: [],
      size: [],
      hay_compression: [],
      moisture: [],
      rain: [],
      storage: [],
      crop: [],
      binding_type: [],
      price_min: 0,
      price_max: 1000,
      shipping_options: [],
      payment_options: [],
      crude_protein_range_ids: [],
    },
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(
    userProfile?.type_ids.includes(2) ? 14 : 15
  );
  const [total, setTotal] = useState(0);
  const [pageSizeOptions, setPageSizeOptions] = useState<string[]>([]);
  const [allAd, setAllAd] = useState<any>(null);
  const { Option } = Select;
  const [sortBy, setSortBy] = useState<any>(null);
  const [sortedProduct, setSortedProduct] = useState<any>(null);

  // FETCH Product List
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await ProductApi.GetAll();
        if (response.status === 200) {
          setAllProducts(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("ProductCatalog.tsx - fetchProducts", error);
      }
    };

    fetchProducts();
  }, []);

  // FETCH PRODUCT NEAR HOME
  useEffect(() => {
    const fetchProductNearBy = async (lat: number, lon: number) => {
      try {
        const response = await ProductApi.NearBy(lat, lon);
        if (response.status === 200) {
          setProductNearBy(response.data);
        }
      } catch (error) {
        Helpers.notification.error("Can't fetch product. Please try again");
        errorLog("ProductDetail.tsx - fetchProductNearBy", error);
      }
    };

    if (isAuthenticated && userProfile) {
      fetchProductNearBy(userProfile.lat, userProfile.lon);
    }
  }, []);

  // FETCH ALL ADS
  useEffect(() => {
    const fetchAllAds = async () => {
      try {
        const response = await AdApi.getAll();
        if (response.status === 200) {
          setAllAd(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can't fetch ads. Please try again");
        errorLog("ProductCatalog.tsx - fetchAllAds", error);
      }
    };

    fetchAllAds();
  }, []);

  useEffect(() => {
    if (total) {
      const maxPageSize = 15;
      const maxMultiple = Math.ceil(total / maxPageSize) * maxPageSize;
      const options = [];

      for (let size = maxPageSize; size <= maxMultiple; size += maxPageSize) {
        userProfile?.type_ids.includes(2)
          ? options.push((size - 1).toString())
          : options.push(size.toString());
      }

      setPageSizeOptions(options);
    }
  }, [total]);

  const handlePageChange = (page: number, size: number) => {
    setCurrent(page);
    setPageSize(size);
  };

  const handleShowSizeChange = (size: any) => {
    setPageSize(size);
  };

  const handleChangeSortBy = (value: string) => {
    setSortBy(value);
    switch (value) {
      case "1":
        setSortedProduct(
          allProducts.sort(
            (item1: any, item2: any) =>
              new Date(item1.create_date).getDate() -
              new Date(item2.create_date).getDate()
          )
        );
        break;
      case "2":
        setSortedProduct(
          allProducts.sort(
            (item1: any, item2: any) =>
              new Date(item2.create_date).getDate() -
              new Date(item1.create_date).getDate()
          )
        );
        break;
      case "3":
        setSortedProduct(
          allProducts.sort((item1: any, item2: any) => {
            return item1.price_per_tons - item2.price_per_tons;
          })
        );
        break;
      case "4":
        setSortedProduct(
          allProducts.sort((item1: any, item2: any) => {
            return item2.price_per_tons - item1.price_per_tons;
          })
        );
        break;
      case "5":
        setSortedProduct(
          allProducts.sort((item1: any, item2: any) => {
            return item1.hay_type.localeCompare(item2.hay_type);
          })
        );
        break;
      case "6":
        setSortedProduct(
          allProducts.sort((item1: any, item2: any) => {
            return item2.hay_type.localeCompare(item1.hay_type);
          })
        );
        break;
      default:
        break;
    }
  };

  const categoryFilteredProducts = allProducts
    ? allProducts.filter((product: any) => {
        if (searchParam.categories_id.length === 0) return true;
        return searchParam.categories_id.includes(product.product_category_id);
      })
    : null;

  const sortedProducts =
    sortBy && categoryFilteredProducts
      ? [...categoryFilteredProducts].sort((item1: any, item2: any) => {
          switch (sortBy) {
            case "1":
              return (
                new Date(item1.create_date).getTime() -
                new Date(item2.create_date).getTime()
              );
            case "2":
              return (
                new Date(item2.create_date).getTime() -
                new Date(item1.create_date).getTime()
              );
            case "3":
              return item1.price_per_tons - item2.price_per_tons;
            case "4":
              return item2.price_per_tons - item1.price_per_tons;
            case "5":
              return item1.hay_type.localeCompare(item2.hay_type);
            case "6":
              return item2.hay_type.localeCompare(item1.hay_type);
            default:
              return 0;
          }
        })
      : categoryFilteredProducts;

  useEffect(() => {
    setTotal(sortedProducts ? sortedProducts.length : 0);
  }, [sortedProducts]);

  useEffect(() => {
    setCurrent(1);
  }, [searchParam.categories_id]);

  const pagedProducts = sortedProducts
    ? sortedProducts.slice((current - 1) * pageSize, current * pageSize)
    : null;

  return (
    <div className="ProductCatalog">
      <div className="container mt-4 mb-5">
        <ProductFilter
          searchParam={searchParam}
          setSearchParam={setSearchParam}
          setIsOpenMap={setIsOpenMap}
          isOpenMap={isOpenMap}
          componentIn={"productCatalog"}
          isLargeMode={isLargeMode}
          setIsLargeMode={setIsLargeMode}
        />
        <div className="catalog-wrap">
          <Row gutter={16}>
            <Col span={isOpenMap ? 12 : 24}>
              <div className={`product-list-wrap ${isOpenMap && "map"}`}>
                {/* {allAd?.length > 0 && (
                  <ProductList
                    isOpenMap={isOpenMap}
                    type="Promoted Products ⚡"
                    products={
                      searchParam.categories_id.length === 0
                        ? allAd.filter((ad: any) => {
                            return (
                              ad.product_ad_status_id === 1 &&
                              ad.user_id !== userProfile?.user_id
                            );
                          })
                        : allAd.filter((ad: any) => {
                            return (
                              ad.product_ad_status_id === 1 &&
                              ad.user_id !== userProfile?.user_id &&
                              ad.product.product_category_id ===
                                searchParam.categories_id[0]
                            );
                          })
                    }
                    isLargeMode={isLargeMode}
                    setIsLargeMode={setIsLargeMode}
                  />
                )} */}

                <ProductList
                  isOpenMap={isOpenMap}
                  type="Product List"
                  isLargeMode={isLargeMode}
                  setIsLargeMode={setIsLargeMode}
                  products={pagedProducts}
                  additional={
                    <Select
                      size="large"
                      className="sort-by-select"
                      variant="filled"
                      value={sortBy}
                      placeholder="Sort by"
                      onChange={handleChangeSortBy}
                    >
                      <Option value="1">Latest</Option>
                      <Option value="2">Oldest</Option>
                      <Option value="3">Lowest Price</Option>
                      <Option value="4">Highest Price</Option>
                      <Option value="5">Alphabetic (A-Z)</Option>
                      <Option value="6">Alphabetic (Z-A)</Option>
                    </Select>
                  }
                />
              </div>
            </Col>
            {isOpenMap && (
              <Col span={12}>
                <div className="product-map-wrap">
                  <ProductMap
                    productNearBy={productNearBy}
                    isProductDetail={false}
                  />
                </div>
              </Col>
            )}
          </Row>
        </div>
      </div>
      <Pagination
        className="pagination"
        total={total}
        showTotal={(total, range) =>
          `${range[0]}-${range[1]} of ${total} items`
        }
        pageSize={pageSize}
        current={current}
        align="center"
        showSizeChanger
        pageSizeOptions={pageSizeOptions}
        onShowSizeChange={handleShowSizeChange}
        onChange={handlePageChange}
      />
    </div>
  );
};

export default ProductCatalog;
